import React, { useState, useRef, useCallback, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useWidgetLayout, SIZE_SPANS, ALLOWED_SIZES } from '../hooks/useWidgetLayout';
import type { WidgetId, WidgetSize } from '../hooks/useWidgetLayout';
import { WeatherWidget } from './WeatherWidget';
import { NextUpAgenda } from './NextUpAgenda';
import { NewsWidget } from './NewsWidget';
import { DynamicButtonGrid } from './DynamicButtonGrid';
import { useTranslation } from '../hooks/useTranslation';

const LONG_PRESS_MS = 1200;
const DRAG_THRESHOLD = 10;

/**
 * Imperative handle exposed by WidgetGrid (T6.1 / D1).
 *
 * Phase 6 adds a visible "Edit layout" pencil affordance on HomePage so
 * users who never discovered the long-press gesture can still reach the
 * widget editor. To toggle the existing internal `editMode` state from
 * outside the component, WidgetGrid now exposes this imperative handle
 * via React.forwardRef + useImperativeHandle.
 *
 * The long-press handler is intentionally preserved as a power-user
 * shortcut — see plan §11 Q5.
 */
export interface WidgetGridHandle {
  isEditMode: () => boolean;
  enterEditMode: () => void;
  exitEditMode: () => void;
  toggleEditMode: () => void;
}

const WIDGET_LABELS: Record<WidgetId, string> = {
  weather: 'Weather',
  agenda: 'Today',
  news: 'News',
  buttons: 'Buttons',
};

function WidgetComponent({ id, size, isEditMode }: { id: WidgetId; size: WidgetSize; isEditMode: boolean }) {
  // Prevent navigation clicks during edit mode
  const blockClick = isEditMode
    ? (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); }
    : undefined;

  return (
    <div onClick={blockClick} style={{ pointerEvents: isEditMode ? 'none' : 'auto', height: '100%' }}>
      {id === 'weather' && <WeatherWidget />}
      {id === 'agenda' && <NextUpAgenda />}
      {id === 'news' && <NewsWidget />}
      {id === 'buttons' && <DynamicButtonGrid />}
    </div>
  );
}

interface WidgetGridProps {
  /**
   * T6.1 / D1: parents (HomePage) subscribe to know when the user is in
   * widget-edit mode so they can swap their "Edit layout" pencil button for
   * a "Done" affordance. Fires on every transition (entered / exited).
   */
  onEditModeChange?: (isEditMode: boolean) => void;
}

/**
 * WidgetGrid — Configurable CSS Grid with drag-and-drop reordering.
 * Long-press (1.2s) to enter edit mode. Tap widget to cycle size. Drag to reorder.
 *
 * T6.1 / D1: also accepts an imperative ref handle (`WidgetGridHandle`)
 * so HomePage's visible pencil button can toggle the same edit mode the
 * long-press triggers internally, plus an `onEditModeChange` callback so
 * HomePage stays visually in sync (pencil ↔ Done).
 */
export const WidgetGrid = forwardRef<WidgetGridHandle, WidgetGridProps>(function WidgetGrid(
  { onEditModeChange },
  ref,
) {
  const { enabledWidgets, cycleWidgetSize, moveWidget, resetLayout } = useWidgetLayout();
  const { t } = useTranslation();
  const [editMode, setEditMode] = useState(false);

  // Expose imperative handle so HomePage's visible pencil button (T6.1 / D1)
  // can flip the internal edit-mode flag without us lifting state up.
  useImperativeHandle(ref, () => ({
    isEditMode: () => editMode,
    enterEditMode: () => setEditMode(true),
    exitEditMode: () => setEditMode(false),
    toggleEditMode: () => setEditMode((prev) => !prev),
  }), [editMode]);

  // Notify parent on every transition (T6.1 / D1). Effect runs only when
  // `editMode` toggles, so we never fire on re-render.
  useEffect(() => {
    onEditModeChange?.(editMode);
  }, [editMode, onEditModeChange]);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dropIdx, setDropIdx] = useState<number | null>(null);

  // ── Long-press detection ────────────────────────────────
  const longPressTimer = useRef<ReturnType<typeof setTimeout>>();
  const pressStart = useRef<{ x: number; y: number } | null>(null);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (editMode) return; // Already in edit mode
    pressStart.current = { x: e.clientX, y: e.clientY };
    longPressTimer.current = setTimeout(() => {
      setEditMode(true);
      // Haptic feedback if available
      if (navigator.vibrate) navigator.vibrate(50);
    }, LONG_PRESS_MS);
  }, [editMode]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!pressStart.current || editMode) return;
    const dx = e.clientX - pressStart.current.x;
    const dy = e.clientY - pressStart.current.y;
    if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD) {
      // User is scrolling, cancel long-press
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
      pressStart.current = null;
    }
  }, [editMode]);

  const handlePointerUp = useCallback(() => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    pressStart.current = null;
  }, []);

  // ── Drag & drop ─────────────────────────────────────────
  const cellRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const handleDragStart = useCallback((idx: number, e: React.PointerEvent) => {
    if (!editMode) return;
    e.preventDefault();
    setDragIdx(idx);
    setDropIdx(idx);
  }, [editMode]);

  const handleDragMove = useCallback((e: React.PointerEvent) => {
    if (dragIdx === null) return;
    e.preventDefault();

    // Find which cell we're over
    const x = e.clientX;
    const y = e.clientY;
    for (const [idx, el] of cellRefs.current.entries()) {
      const rect = el.getBoundingClientRect();
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
        if (idx !== dropIdx) setDropIdx(idx);
        break;
      }
    }
  }, [dragIdx, dropIdx]);

  const handleDragEnd = useCallback(() => {
    if (dragIdx !== null && dropIdx !== null && dragIdx !== dropIdx) {
      moveWidget(dragIdx, dropIdx);
    }
    setDragIdx(null);
    setDropIdx(null);
  }, [dragIdx, dropIdx, moveWidget]);

  // Listen for pointer move/up on document during drag
  useEffect(() => {
    if (dragIdx === null) return;
    const onMove = (e: PointerEvent) => {
      const x = e.clientX;
      const y = e.clientY;
      for (const [idx, el] of cellRefs.current.entries()) {
        const rect = el.getBoundingClientRect();
        if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
          setDropIdx(prev => prev !== idx ? idx : prev);
          break;
        }
      }
    };
    const onUp = () => {
      if (dragIdx !== null) {
        setDropIdx(prev => {
          if (prev !== null && prev !== dragIdx) {
            moveWidget(dragIdx, prev);
          }
          return null;
        });
      }
      setDragIdx(null);
    };
    document.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerup', onUp);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
  }, [dragIdx, moveWidget]);

  const handleWidgetTap = useCallback((id: WidgetId) => {
    if (!editMode) return;
    cycleWidgetSize(id);
    if (navigator.vibrate) navigator.vibrate(30);
  }, [editMode, cycleWidgetSize]);

  return (
    <div
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {/* CSS Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '12px',
      }}>
        {enabledWidgets.map((widget, idx) => {
          const spans = SIZE_SPANS[widget.size];
          const isDragging = dragIdx === idx;
          const isDropTarget = dropIdx === idx && dragIdx !== null && dragIdx !== idx;

          return (
            <div
              key={widget.id}
              ref={(el) => {
                if (el) cellRefs.current.set(idx, el);
                else cellRefs.current.delete(idx);
              }}
              onPointerDown={(e) => {
                if (editMode) handleDragStart(idx, e);
              }}
              onClick={() => handleWidgetTap(widget.id)}
              style={{
                gridColumn: `span ${spans.col}`,
                gridRow: `span ${spans.row}`,
                position: 'relative',
                opacity: isDragging ? 0.5 : 1,
                transition: 'opacity 0.2s, transform 0.15s',
                animation: editMode ? 'widgetWiggle 0.3s ease-in-out infinite alternate' : 'none',
                outline: isDropTarget ? '3px dashed var(--caro-primary)' : 'none',
                outlineOffset: '4px',
                borderRadius: '16px',
              }}
            >
              <WidgetComponent id={widget.id} size={widget.size} isEditMode={editMode} />
              {/* Edit mode overlay */}
              {editMode && (
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '16px',
                  border: '2px dashed var(--caro-primary)',
                  // T6.8 / D9: was rgba(37, 99, 235, 0.08) — an off-brand blue
                  // that fought the plum (or yellow in HC) primary. color-mix
                  // derives the tint from the resolved CSS variable so the
                  // halo follows building branding and theme automatically.
                  backgroundColor: 'color-mix(in srgb, var(--caro-primary) 8%, transparent)',
                  pointerEvents: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  padding: '8px',
                }}>
                  {/* Size label */}
                  <span style={{
                    backgroundColor: 'var(--caro-primary)',
                    color: 'white',
                    fontSize: 'var(--font-small)',
                    fontWeight: 700,
                    padding: '3px 10px',
                    borderRadius: '8px',
                  }}>
                    {WIDGET_LABELS[widget.id]} &middot; {widget.size}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Edit mode controls */}
      {editMode && (
        <div style={{
          position: 'fixed',
          bottom: '90px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '12px',
          zIndex: 41,
        }}>
          <button
            onClick={() => {
              resetLayout();
              if (navigator.vibrate) navigator.vibrate(30);
            }}
            style={{
              padding: '12px 24px',
              borderRadius: '24px',
              border: '2px solid rgba(255,255,255,0.3)',
              backgroundColor: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(8px)',
              color: 'white',
              fontSize: 'var(--font-body)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Reset
          </button>
          <button
            onClick={() => setEditMode(false)}
            style={{
              padding: '12px 32px',
              borderRadius: '24px',
              border: 'none',
              backgroundColor: 'var(--caro-primary)',
              color: 'white',
              fontSize: 'var(--font-body)',
              fontWeight: 700,
              cursor: 'pointer',
              // T6.8 / D9: was rgba(37, 99, 235, 0.4) — an off-brand blue.
              // Now derives from the resolved primary so the Done button glow
              // matches building branding (or yellow in HC mode).
              boxShadow: '0 4px 16px color-mix(in srgb, var(--caro-primary) 40%, transparent)',
            }}
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
});
